import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';
import { COLORS } from '../constants';

interface AnimatedTextProps {
  text: string;
  startFrame?: number;
  fontSize?: number;
  color?: string;
  fontWeight?: number;
  textAlign?: 'left' | 'center' | 'right';
  style?: React.CSSProperties;
  mode?: 'fadeUp' | 'typewriter' | 'wordByWord' | 'scaleIn';
  lineHeight?: number;
}

export const AnimatedText: React.FC<AnimatedTextProps> = ({
  text,
  startFrame = 0,
  fontSize = 48,
  color = COLORS.white,
  fontWeight = 600,
  textAlign = 'center',
  style = {},
  mode = 'fadeUp',
  lineHeight = 1.3,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - startFrame;

  if (f < 0) return null;

  if (mode === 'fadeUp') {
    const opacity = interpolate(f, [0, 20], [0, 1], {
      extrapolateRight: 'clamp',
    });
    const y = interpolate(f, [0, 20], [40, 0], {
      extrapolateRight: 'clamp',
    });
    return (
      <div
        style={{
          fontSize,
          fontWeight,
          color,
          textAlign,
          lineHeight,
          opacity,
          transform: `translateY(${y}px)`,
          fontFamily: 'Inter, system-ui, sans-serif',
          ...style,
        }}
      >
        {text}
      </div>
    );
  }

  if (mode === 'typewriter') {
    const charsToShow = Math.floor(
      interpolate(f, [0, text.length * 1.5], [0, text.length], {
        extrapolateRight: 'clamp',
      })
    );
    return (
      <div
        style={{
          fontSize,
          fontWeight,
          color,
          textAlign,
          lineHeight,
          fontFamily: 'Inter, system-ui, sans-serif',
          ...style,
        }}
      >
        {text.slice(0, charsToShow)}
        <span
          style={{
            opacity: f % 20 > 10 ? 1 : 0,
            color: COLORS.primary,
          }}
        >
          |
        </span>
      </div>
    );
  }

  if (mode === 'wordByWord') {
    const words = text.split(' ');
    return (
      <div
        style={{
          fontSize,
          fontWeight,
          textAlign,
          lineHeight,
          fontFamily: 'Inter, system-ui, sans-serif',
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent:
            textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start',
          gap: `0 ${fontSize * 0.3}px`,
          ...style,
        }}
      >
        {words.map((word, i) => {
          const wordDelay = i * 5;
          const wordOpacity = interpolate(f - wordDelay, [0, 10], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const wordY = interpolate(f - wordDelay, [0, 10], [20, 0], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <span
              key={i}
              style={{
                color,
                opacity: wordOpacity,
                transform: `translateY(${wordY}px)`,
                display: 'inline-block',
              }}
            >
              {word}
            </span>
          );
        })}
      </div>
    );
  }

  // scaleIn
  const scale = spring({
    frame: f,
    fps,
    config: { damping: 12, stiffness: 100, mass: 0.5 },
  });
  const opacity = interpolate(f, [0, 15], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        fontSize,
        fontWeight,
        color,
        textAlign,
        lineHeight,
        opacity,
        transform: `scale(${scale})`,
        fontFamily: 'Inter, system-ui, sans-serif',
        ...style,
      }}
    >
      {text}
    </div>
  );
};

interface GradientTextProps {
  text: string;
  startFrame?: number;
  fontSize?: number;
  fontWeight?: number;
  gradient?: string;
  style?: React.CSSProperties;
}

export const GradientText: React.FC<GradientTextProps> = ({
  text,
  startFrame = 0,
  fontSize = 72,
  fontWeight = 800,
  gradient = COLORS.gradientBlue,
  style = {},
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const f = frame - startFrame;

  if (f < 0) return null;

  const scale = spring({
    frame: f,
    fps,
    config: { damping: 15, stiffness: 80, mass: 0.8 },
  });
  const opacity = interpolate(f, [0, 20], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        fontSize,
        fontWeight,
        textAlign: 'center',
        background: gradient,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        opacity,
        transform: `scale(${scale})`,
        fontFamily: 'Inter, system-ui, sans-serif',
        lineHeight: 1.1,
        ...style,
      }}
    >
      {text}
    </div>
  );
};
